import React, { useEffect, useState } from "react";
import {
  Typography,
  Switch,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  OutlinedInput,
} from "@mui/material";
import {
  AddCircleOutlineOutlined,
  RemoveCircleOutlineOutlined,
} from "@mui/icons-material";
import styles from "./feeconfig.module.css";
import config from "../../../../service/config";
import { useAllProduct } from "../../../../api-context/commonApiContext";
import FullScreenLoader from "../../../../reusable/loader/FullScreenLoader";

const paymentModes = ["UPI", "NEFT", "IMPS", "RTGS", "FT"];

const FeeConfig = ({ next, selectedEscrow }) => {
  const { loading, setLoading } = useAllProduct();
  const [feeMethod, setFeeMethod] = useState("Deduct from collect only");

  // State per payment mode
  const [enabledModes, setEnabledModes] = useState({
    UPI: false,
    NEFT: false,
    IMPS: false,
    RTGS: false,
    FT: false,
    ECollect: false,
  });

  const [feeType, setFeeType] = useState({});
  const [tierType, setTierType] = useState({});
  const [tiers, setTiers] = useState({});
  const [feeAmount, setFeeAmount] = useState({});
  const [additionalFee, setAdditionalFee] = useState("");
  const feeMethodModesMap = {
    "Deduct from collect only": ["UPI", "ECollect"],
    "Deduct from payout only": ["UPI", "NEFT", "IMPS", "RTGS", "FT"],
    "Deduct from both collect and payout": [
      "UPI",
      "NEFT",
      "IMPS",
      "RTGS",
      "FT",
      "ECollect",
    ],
  };

  useEffect(() => {
    const allowedModes = feeMethodModesMap[feeMethod];

    const filterByAllowedModes = (obj) =>
      Object.fromEntries(
        Object.entries(obj).filter(([key]) => allowedModes.includes(key))
      );

    setEnabledModes((prev) => filterByAllowedModes(prev));
    setFeeType((prev) => filterByAllowedModes(prev));
    setTierType((prev) => filterByAllowedModes(prev));
    setTiers((prev) => filterByAllowedModes(prev));
    setFeeAmount((prev) => filterByAllowedModes(prev));
  }, [feeMethod]);

  useEffect(() => {
    if (selectedEscrow?.fee_config) {
      const config = selectedEscrow.fee_config;

      setFeeMethod(config.fee_method || "Deduct from collect only");
      setEnabledModes(config.enabled_modes || {});
      setFeeType(config.fee_type || {});
      setTierType(config.tier_type || {});
      setTiers(config.tiers || {});
      setFeeAmount(config.fee_amount || {});
      setAdditionalFee(config.additional_fee || "");
    }
  }, [selectedEscrow]);

  const toggleMode = (mode) => {
    setEnabledModes((prev) => {
      const updated = { ...prev, [mode]: !prev[mode] };

      // If mode is turned off, clear related state
      if (!updated[mode]) {
        setFeeType((prev) => {
          const copy = { ...prev };
          delete copy[mode];
          return copy;
        });
        setTierType((prev) => {
          const copy = { ...prev };
          delete copy[mode];
          return copy;
        });
        setTiers((prev) => {
          const copy = { ...prev };
          delete copy[mode];
          return copy;
        });
        setFeeAmount((prev) => {
          const copy = { ...prev };
          delete copy[mode];
          return copy;
        });
      }

      return updated;
    });
  };

  const updateTier = (mode, index, field, value) => {
    const updated = [...(tiers[mode] || [{ lte: "", amount: "" }])];
    updated[index][field] = value;
    setTiers((prev) => ({ ...prev, [mode]: updated }));
  };

  const clearTiers = (mode) => {
    setTiers((prev) => ({ ...prev, [mode]: [{ lte: "", amount: "" }] }));
  };

  const addTier = (mode) => {
    const current = tiers[mode] || [];
    setTiers((prev) => ({
      ...prev,
      [mode]: [...current, { lte: "", amount: "" }],
    }));
  };

  const renderFeeBlock = (mode) => (
    <div className={styles.modeBlock}>
      <Typography
        variant="h6"
        className={styles.sectionHeader}
        sx={{
          backgroundColor: "#8F8F8F",
          textDecoration: "underline",
          padding: "8px",
        }}
      >
        {mode}
      </Typography>

      <div className="d-flex align-items-center mt-2">
        <div className="w-25">
          <Typography variant="body1" fontWeight={500} color="#4C4E64">
            Fee Type<span style={{ color: "#DC3545" }}>*</span>
          </Typography>
        </div>
        <div>
          <FormControl
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0 !important",
              },
              width: "175px",
            }}
          >
            <Select
              value={feeType[mode] || ""}
              onChange={(e) =>
                setFeeType((prev) => ({ ...prev, [mode]: e.target.value }))
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              <MenuItem value="Tier">Tier</MenuItem>
              <MenuItem value="Percentage">Percentage</MenuItem>
              <MenuItem value="Flat">Flat</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {feeType[mode] === "Tier" && (
        <>
          <div className="d-flex align-items-center mt-2">
            <div className="w-25">
              <Typography variant="body1" fontWeight={500} color="#4C4E64">
                Select <span style={{ color: "#DC3545" }}>*</span>
              </Typography>
            </div>
            <div>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={tierType[mode] || "flat"}
                  onChange={(e) =>
                    setTierType((prev) => ({ ...prev, [mode]: e.target.value }))
                  }
                >
                  <FormControlLabel
                    value="flat"
                    control={<Radio />}
                    label="Flat"
                  />
                  <FormControlLabel
                    value="percent"
                    control={<Radio />}
                    label="Percentage"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>

          {(tiers[mode] || [{ lte: "", amount: "" }]).map((tier, index) => (
            <div key={index}>
              <div className="d-flex align-items-center mt-2">
                <Typography variant="h6" color="#4C4E64" className="w-25">
                  {index + 1}. Tier
                </Typography>
                {index > 0 ? (
                  <Button
                    className="mx-5"
                    onClick={() =>
                      setTiers((prev) => ({
                        ...prev,
                        [mode]: prev[mode].filter((_, i) => i !== index),
                      }))
                    }
                    sx={{ textTransform: "none", color: "#4C4E64" }}
                    endIcon={
                      <RemoveCircleOutlineOutlined style={{ color: "red" }} />
                    }
                  >
                    Remove
                  </Button>
                ) : (
                  <Typography
                    variant="body2"
                    className={`${styles.clearButton} mx-5`}
                    onClick={() => clearTiers(mode)}
                    sx={{
                      textTransform: "none",
                      color: "#4C4E64",
                      textDecoration: "underline",
                    }}
                  >
                    Clear
                  </Typography>
                )}
              </div>

              <div className="d-flex align-items-center">
                <div className="w-25">
                  <Typography variant="body1" fontWeight={500} color="#4C4E64">
                    Less than equal to{" "}
                    <span style={{ color: "#DC3545" }}>*</span>
                  </Typography>
                </div>
                <TextField
                  value={tier.lte}
                  placeholder="Enter amount"
                  onChange={(e) =>
                    updateTier(mode, index, "lte", e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0 !important",
                    },
                    width: "175px",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className="d-flex align-items-center mt-2">
                <div className="w-25">
                  <Typography variant="body1" fontWeight={500} color="#4C4E64">
                    Fee Amount <span style={{ color: "#DC3545" }}>*</span>
                  </Typography>
                </div>
                <TextField
                  value={tier.amount}
                  placeholder="Enter amount"
                  onChange={(e) =>
                    updateTier(mode, index, "amount", e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0 !important",
                    },
                    width: "175px",
                  }}
                  InputProps={{
                    [tierType[mode] === "flat"
                      ? "startAdornment"
                      : "endAdornment"]: (
                      <InputAdornment
                        position={tierType[mode] === "flat" ? "start" : "end"}
                      >
                        {tierType[mode] === "flat" ? "₹" : "%"}
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineOutlined />}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0 !important",
              },
              marginLeft: "24px",
              marginTop: "16px",
              width: "192px",
              color: "#2348AB",
              border: "1px solid #2348AB",
            }}
            onClick={() => addTier(mode)}
          >
            Add Tier
          </Button>
        </>
      )}

      {(feeType[mode] === "Flat" || feeType[mode] === "Percentage") && (
        <div className="d-flex align-items-center mt-2">
          <div className="w-25">
            <Typography variant="body1" fontWeight={500} color="#4C4E64">
              Fee Amount <span style={{ color: "#DC3545" }}>*</span>
            </Typography>
          </div>
          <TextField
            placeholder="Enter amount"
            value={feeAmount[mode] || ""}
            onChange={(e) =>
              setFeeAmount((prev) => ({ ...prev, [mode]: e.target.value }))
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0 !important",
              },
              width: "175px",
            }}
            InputProps={{
              [feeType[mode] === "Flat" ? "startAdornment" : "endAdornment"]: (
                <InputAdornment
                  position={feeType[mode] === "Flat" ? "start" : "end"}
                >
                  {feeType[mode] === "Flat" ? "₹" : "%"}
                </InputAdornment>
              ),
            }}
          />
        </div>
      )}
    </div>
  );

  const renderSection = (label, modes) => (
    <>
      <div className={styles.modeBlock}>
        <h6>{label} Fee method & Modes</h6>
        <div className={styles.sectionFlexWrap}>
          {modes.map((mode) => enabledModes[mode] && renderFeeBlock(mode))}
        </div>
      </div>
    </>
  );

  const handleSave = () => {
    setLoading(true);
    const modesToKeep = {
      "Deduct from collect only": ["UPI", "ECollect"],
      "Deduct from payout only": ["UPI", "NEFT", "IMPS", "RTGS", "FT"],
      "Deduct from both collect and payout": [
        "UPI",
        "NEFT",
        "IMPS",
        "RTGS",
        "FT",
        "ECollect",
      ],
    };

    const allowedModes = modesToKeep[feeMethod];

    // Filter states to keep only the relevant modes
    const filteredEnabledModes = Object.fromEntries(
      Object.entries(enabledModes).filter(([mode]) =>
        allowedModes.includes(mode)
      )
    );

    const filteredFeeType = Object.fromEntries(
      Object.entries(feeType).filter(([mode]) => allowedModes.includes(mode))
    );

    const filteredTierType = Object.fromEntries(
      Object.entries(tierType).filter(([mode]) => allowedModes.includes(mode))
    );

    const filteredTiers = Object.fromEntries(
      Object.entries(tiers).filter(([mode]) => allowedModes.includes(mode))
    );

    const filteredFeeAmount = Object.fromEntries(
      Object.entries(feeAmount).filter(([mode]) => allowedModes.includes(mode))
    );

    const payload = {
      client_id: selectedEscrow.client_id,
      main_escrow_id: selectedEscrow.main_escrow_id,
      config: {
        fee_method: feeMethod,
        enabled_modes: filteredEnabledModes,
        fee_type: filteredFeeType,
        tier_type: filteredTierType,
        tiers: filteredTiers,
        fee_amount: filteredFeeAmount,
        additional_fee: additionalFee,
      },
    };

    console.log("Saving Fee Config:", payload);

    fetch(`${config.node_api_endpoint}/api/escrow-fee-config`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_API_KEY,
      },

      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          next();
        } else {
          alert(result.message);
        }
      })
      .catch((err) => {
        console.error("Failed to save fee config:", err);
      })
      .finally(() => {
        setLoading(false); // End loader
      });
  };

  return (
    <>
      <FullScreenLoader spinning={loading} />
      <div className={styles.feeConfigContainer}>
        <h6>Select Fee method & Modes</h6>
        <FormControl
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "0 !important",
            },
            width: "300px",
          }}
        >
          <Select
            value={feeMethod}
            onChange={(e) => setFeeMethod(e.target.value)}
            displayEmpty
            required
          >
            <MenuItem value="Deduct from collect only">
              Deduct from collect only
            </MenuItem>
            <MenuItem value="Deduct from payout only">
              Deduct from payout only
            </MenuItem>
            <MenuItem value="Deduct from both collect and payout">
              Deduct from both collect and payout
            </MenuItem>
          </Select>
        </FormControl>

        {(feeMethod === "Deduct from collect only" ||
          feeMethod === "Deduct from both collect and payout") && (
          <>
            {/*  */}
            <h6 className="mt-4">Collect</h6>
            <div className={styles.toggleRow}>
              <Typography variant="body2">UPI</Typography>
              <Switch
                checked={enabledModes.UPI}
                onChange={() => toggleMode("UPI")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#ffffff",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2348AB",
                  },
                }}
              />
              <div className={styles.divider}></div>
              <Typography variant="body2">ECollect</Typography>
              <Switch
                checked={enabledModes.ECollect}
                onChange={() => toggleMode("ECollect")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#ffffff",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2348AB",
                  },
                }}
              />
            </div>
            {renderSection("Collect", ["UPI", "ECollect"])}
          </>
        )}

        {(feeMethod === "Deduct from payout only" ||
          feeMethod === "Deduct from both collect and payout") && (
          <>
            <h6 className="mt-4">Payout</h6>
            <div className={styles.toggleRow}>
              {["UPI", "NEFT", "IMPS", "RTGS", "FT"].map((mode) => (
                <React.Fragment key={mode}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabledModes[mode]}
                        onChange={() => toggleMode(mode)}
                      />
                    }
                    label={mode}
                  />
                  <div className={styles.divider}></div>
                </React.Fragment>
              ))}
            </div>
            {renderSection("Payout", ["UPI", "NEFT", "IMPS", "RTGS", "FT"])}
          </>
        )}

        <h6>Add on fee (Optional)</h6>
        <div className="d-flex align-items-center mt-2 w-50">
          <div className="w-25">
            <Typography variant="body1" fontWeight={500} color="#4C4E64">
              Add on / Top up{/* <span style={{ color: "#DC3545" }}>*</span> */}
            </Typography>
          </div>
          <div>
            <TextField
              placeholder="Enter amount"
              value={additionalFee}
              onChange={(e) => setAdditionalFee(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0 !important",
                },
                width: "175px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          </div>
        </div>
        <p className="text-muted mt-1">
          <small>
            Note: The minimum Fee will be applied to whichever fees are higher.
          </small>
        </p>

        <div className="mt-4">
          <Button
            disableElevation
            variant="contained"
            sx={{
              borderRadius: "8px",
              height: 40,
              backgroundColor: "#2348AB",
              color: "#ffffff",
            }}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </>
  );
};

export default FeeConfig;
